import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Eye, Edit, Trash2, Mail, Phone, BarChart3, 
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Building2, User, Landmark, Filter, X, Check, Users, Merge
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { contactosAPI } from '../api';
import { toast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import ContactoDuplicadosDialog from '../components/ContactoDuplicadosDialog';
import VoiceSearchButton from '../components/VoiceSearchButton';

// Tipos de entidad
const TIPOS_ENTIDAD = [
  { codigo: 'persona', nombre: 'Persona Física', icon: User },
  { codigo: 'organismo', nombre: 'Organismo', icon: Landmark },
  { codigo: 'empresa', nombre: 'Empresa', icon: Building2 }
];

// Subtipos organizados por tipo de entidad (Quolaw)
const SUBTIPOS_POR_ENTIDAD = {
  persona: [
    { codigo: 'cliente', nombre: 'Cliente' },
    { codigo: 'potencial_cliente', nombre: 'Potencial Cliente' },
    { codigo: 'abogado_propio', nombre: 'Abogado Propio' },
    { codigo: 'abogado_contrario', nombre: 'Abogado Contrario' },
    { codigo: 'abogado_colaborador', nombre: 'Abogado Colaborador' },
    { codigo: 'procurador_propio', nombre: 'Procurador Propio' },
    { codigo: 'procurador_contrario', nombre: 'Procurador Contrario' },
    { codigo: 'parte_contraria', nombre: 'Parte Contraria' },
    { codigo: 'asesor', nombre: 'Asesor' },
    { codigo: 'colaborador', nombre: 'Colaborador' },
    { codigo: 'gestor', nombre: 'Gestor' },
    { codigo: 'intermediario', nombre: 'Intermediario' },
    { codigo: 'otorgante', nombre: 'Otorgante' },
    { codigo: 'perito', nombre: 'Perito' },
    { codigo: 'testigo_propio', nombre: 'Testigo Propio' },
    { codigo: 'testigo_contrario', nombre: 'Testigo Parte Contraria' },
    { codigo: 'proveedor', nombre: 'Proveedor' },
    { codigo: 'otro', nombre: 'Otro' }
  ],
  empresa: [
    { codigo: 'cliente', nombre: 'Cliente' },
    { codigo: 'potencial_cliente', nombre: 'Potencial Cliente' },
    { codigo: 'parte_contraria', nombre: 'Parte Contraria' },
    { codigo: 'sociedad_limitada', nombre: 'Sociedad Limitada' },
    { codigo: 'sociedad_anonima', nombre: 'Sociedad Anónima' },
    { codigo: 'comunidad_bienes', nombre: 'Comunidad de Bienes' },
    { codigo: 'sociedad_civil', nombre: 'Sociedad Civil' },
    { codigo: 'cooperativa', nombre: 'Cooperativa' },
    { codigo: 'asesoria', nombre: 'Asesoría' },
    { codigo: 'gestoria', nombre: 'Gestoría' },
    { codigo: 'aseguradora', nombre: 'Aseguradora' },
    { codigo: 'inmobiliaria', nombre: 'Inmobiliaria' },
    { codigo: 'colaborador', nombre: 'Colaborador' },
    { codigo: 'otorgante', nombre: 'Otorgante' },
    { codigo: 'perito', nombre: 'Perito' },
    { codigo: 'proveedor', nombre: 'Proveedor' },
    { codigo: 'otro', nombre: 'Otro' }
  ],
  organismo: [
    { codigo: 'juzgado', nombre: 'Juzgado' },
    { codigo: 'organismo', nombre: 'Organismo' },
    { codigo: 'notaria', nombre: 'Notaría' },
    { codigo: 'registro', nombre: 'Registro' },
    { codigo: 'administracion_publica', nombre: 'Administración Pública' },
    { codigo: 'diputacion', nombre: 'Diputación' },
    { codigo: 'cliente', nombre: 'Cliente' },
    { codigo: 'proveedor', nombre: 'Proveedor' },
    { codigo: 'otro', nombre: 'Otro' }
  ]
};

// Lista plana de todos los subtipos (para la tabla principal)
const SUBTIPOS = [
  ...SUBTIPOS_POR_ENTIDAD.persona,
  ...SUBTIPOS_POR_ENTIDAD.empresa.filter(s => !SUBTIPOS_POR_ENTIDAD.persona.find(p => p.codigo === s.codigo)),
  ...SUBTIPOS_POR_ENTIDAD.organismo.filter(s => !SUBTIPOS_POR_ENTIDAD.persona.find(p => p.codigo === s.codigo) && !SUBTIPOS_POR_ENTIDAD.empresa.find(e => e.codigo === s.codigo))
];

const ESTADOS_CIVILES = ['Soltero/a', 'Casado/a', 'Divorciado/a', 'Viudo/a', 'Separado/a', 'Pareja de hecho', 'Otro'];
const CANALES_ENTRADA = ['Recomendación', 'Internet', 'Redes sociales', 'Publicidad', 'Directorio profesional', 'Antiguo cliente', 'Otro'];

// Formulario de contacto en pasos (Wizard)
export const ContactoWizard = ({ isOpen, onClose, contacto = null, onSaved }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Información básica
    tipo_entidad: 'persona',
    tipo: 'cliente',
    nombre: '',
    primer_apellido: '',
    segundo_apellido: '',
    razon_social: '',
    nombre_comercial: '',
    nacionalidad: '',
    nif_cif: '',
    sitio_web: '',
    cargo: '',
    fecha_nacimiento: '',
    canal_entrada: '',
    numero_seguridad_social: '',
    estado_civil: '',
    // Dirección
    direccion: '',
    direccion_linea2: '',
    ciudad: '',
    codigo_postal: '',
    provincia: '',
    pais: 'España',
    // Contacto
    email: '',
    email_secundario: '',
    telefono: '',
    telefono_secundario: '',
    telefono_movil: '',
    fax: '',
    // Colegiado
    numero_colegiado: '',
    colegio_profesional: '',
    fecha_colegiacion: '',
    // Bancarios
    iban: '',
    swift_bic: '',
    banco: '',
    formas_pago: '',
    precio_hora: '',
    precio_hora_responsable: '',
    precio_hora_tipo: '',
    facturable: true,
    // Otros
    notas: '',
    observaciones: '',
    etiquetas: ''
  });

  useEffect(() => {
    if (contacto) {
      setFormData({
        ...formData,
        ...contacto,
        fecha_nacimiento: contacto.fecha_nacimiento || '',
        fecha_colegiacion: contacto.fecha_colegiacion || '',
        precio_hora: contacto.precio_hora || ''
      });
    } else {
      // Reset form
      setFormData({
        tipo_entidad: 'persona',
        tipo: 'cliente',
        nombre: '',
        primer_apellido: '',
        segundo_apellido: '',
        razon_social: '',
        nombre_comercial: '',
        nacionalidad: '',
        nif_cif: '',
        sitio_web: '',
        cargo: '',
        fecha_nacimiento: '',
        canal_entrada: '',
        numero_seguridad_social: '',
        estado_civil: '',
        direccion: '',
        direccion_linea2: '',
        ciudad: '',
        codigo_postal: '',
        provincia: '',
        pais: 'España',
        email: '',
        email_secundario: '',
        telefono: '',
        telefono_secundario: '',
        telefono_movil: '',
        fax: '',
        numero_colegiado: '',
        colegio_profesional: '',
        fecha_colegiacion: '',
        iban: '',
        swift_bic: '',
        banco: '',
        formas_pago: '',
        precio_hora: '',
        precio_hora_responsable: '',
        precio_hora_tipo: '',
        facturable: true,
        notas: '',
        observaciones: '',
        etiquetas: ''
      });
    }
    setStep(1);
  }, [contacto, isOpen]);

  const handleChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Si cambia el tipo de entidad, resetear el subtipo al primero de la lista
      if (field === 'tipo_entidad') {
        const subtiposDisponibles = SUBTIPOS_POR_ENTIDAD[value] || [];
        if (subtiposDisponibles.length > 0) {
          updated.tipo = subtiposDisponibles[0].codigo;
        }
      }
      
      return updated;
    });
  };

  // Obtener subtipos disponibles según el tipo de entidad actual
  const getSubtiposDisponibles = () => {
    return SUBTIPOS_POR_ENTIDAD[formData.tipo_entidad] || SUBTIPOS_POR_ENTIDAD.persona;
  };

  const handleSave = async () => {
    if (!formData.nombre && formData.tipo_entidad === 'persona') {
      toast({ title: 'Error', description: 'El nombre es obligatorio', variant: 'destructive' });
      return;
    }
    if (!formData.razon_social && formData.tipo_entidad !== 'persona') {
      toast({ title: 'Error', description: 'La razón social es obligatoria', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const dataToSend = {
        ...formData,
        precio_hora: formData.precio_hora ? parseFloat(formData.precio_hora) : null,
        fecha_nacimiento: formData.fecha_nacimiento || null,
        fecha_colegiacion: formData.fecha_colegiacion || null
      };

      if (contacto) {
        await contactosAPI.update(contacto.id, dataToSend);
        toast({ title: 'Contacto actualizado', description: 'Los datos se han guardado correctamente' });
      } else {
        await contactosAPI.create(dataToSend);
        toast({ title: 'Contacto creado', description: 'El contacto se ha creado correctamente' });
      }
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Error:', error);
      toast({ title: 'Error', description: 'No se pudo guardar el contacto', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const steps = [
    { num: 1, title: 'Información básica', icon: User },
    { num: 2, title: 'Dirección', icon: Building2 },
    { num: 3, title: 'Datos de contacto', icon: Mail },
    { num: 4, title: 'Datos del colegiado', icon: Landmark },
    { num: 5, title: 'Datos bancarios', icon: Building2 },
    { num: 6, title: 'Otros', icon: Edit }
  ];

  const renderStep1 = () => (
    <div className="space-y-4">
      {/* Tipo de entidad */}
      <div>
        <Label className="text-sm font-medium">Tipo de contacto</Label>
        <div className="grid grid-cols-3 gap-3 mt-2">
          {TIPOS_ENTIDAD.map(tipo => {
            const Icon = tipo.icon;
            const isSelected = formData.tipo_entidad === tipo.codigo;
            return (
              <button
                key={tipo.codigo}
                type="button"
                onClick={() => handleChange('tipo_entidad', tipo.codigo)}
                className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                  isSelected 
                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon className={`w-6 h-6 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                <span className="text-sm font-medium">{tipo.nombre}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Subtipo/Rol */}
      <div>
        <Label>Subtipo de contacto</Label>
        <Select value={formData.tipo} onValueChange={(v) => handleChange('tipo', v)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {getSubtiposDisponibles().map(s => (
              <SelectItem key={s.codigo} value={s.codigo}>{s.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Campos según tipo de entidad */}
      {formData.tipo_entidad === 'persona' ? (
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Nombre *</Label>
            <Input 
              value={formData.nombre} 
              onChange={(e) => handleChange('nombre', e.target.value)}
              placeholder="Nombre"
            />
          </div>
          <div>
            <Label>Primer apellido</Label>
            <Input 
              value={formData.primer_apellido} 
              onChange={(e) => handleChange('primer_apellido', e.target.value)}
            />
          </div>
          <div>
            <Label>Segundo apellido</Label>
            <Input 
              value={formData.segundo_apellido} 
              onChange={(e) => handleChange('segundo_apellido', e.target.value)}
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Razón social *</Label>
            <Input 
              value={formData.razon_social} 
              onChange={(e) => {
                handleChange('razon_social', e.target.value);
                handleChange('nombre', e.target.value);
              }}
              placeholder="Razón social de la entidad"
            />
          </div>
          <div>
            <Label>Nombre comercial</Label>
            <Input 
              value={formData.nombre_comercial} 
              onChange={(e) => handleChange('nombre_comercial', e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Nacionalidad</Label>
          <Input 
            value={formData.nacionalidad} 
            onChange={(e) => handleChange('nacionalidad', e.target.value)}
          />
        </div>
        <div>
          <Label>CIF/NIF</Label>
          <Input 
            value={formData.nif_cif} 
            onChange={(e) => handleChange('nif_cif', e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Sitio web</Label>
          <Input 
            value={formData.sitio_web} 
            onChange={(e) => handleChange('sitio_web', e.target.value)}
            placeholder="https://"
          />
        </div>
        <div>
          <Label>Cargo</Label>
          <Input 
            value={formData.cargo} 
            onChange={(e) => handleChange('cargo', e.target.value)}
          />
        </div>
      </div>

      {formData.tipo_entidad === 'persona' && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha de nacimiento</Label>
              <Input 
                type="date"
                value={formData.fecha_nacimiento} 
                onChange={(e) => handleChange('fecha_nacimiento', e.target.value)}
              />
            </div>
            <div>
              <Label>Canal de entrada</Label>
              <Select value={formData.canal_entrada} onValueChange={(v) => handleChange('canal_entrada', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {CANALES_ENTRADA.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Nº Seguridad Social</Label>
              <Input 
                value={formData.numero_seguridad_social} 
                onChange={(e) => handleChange('numero_seguridad_social', e.target.value)}
              />
            </div>
            <div>
              <Label>Estado civil</Label>
              <Select value={formData.estado_civil} onValueChange={(v) => handleChange('estado_civil', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar..." />
                </SelectTrigger>
                <SelectContent>
                  {ESTADOS_CIVILES.map(e => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div>
        <Label>Dirección</Label>
        <Input 
          value={formData.direccion} 
          onChange={(e) => handleChange('direccion', e.target.value)}
          placeholder="Calle, número..."
        />
      </div>
      <div>
        <Label>Dirección (línea 2)</Label>
        <Input 
          value={formData.direccion_linea2} 
          onChange={(e) => handleChange('direccion_linea2', e.target.value)}
          placeholder="Piso, puerta, escalera..."
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Ciudad</Label>
          <Input 
            value={formData.ciudad} 
            onChange={(e) => handleChange('ciudad', e.target.value)}
          />
        </div>
        <div>
          <Label>Código postal</Label>
          <Input 
            value={formData.codigo_postal} 
            onChange={(e) => handleChange('codigo_postal', e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Provincia</Label>
          <Input 
            value={formData.provincia} 
            onChange={(e) => handleChange('provincia', e.target.value)}
          />
        </div>
        <div>
          <Label>País</Label>
          <Input 
            value={formData.pais} 
            onChange={(e) => handleChange('pais', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Teléfono principal</Label>
          <Input 
            value={formData.telefono} 
            onChange={(e) => handleChange('telefono', e.target.value)}
            placeholder="+34..."
          />
        </div>
        <div>
          <Label>Teléfono secundario</Label>
          <Input 
            value={formData.telefono_secundario} 
            onChange={(e) => handleChange('telefono_secundario', e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Teléfono móvil</Label>
          <Input 
            value={formData.telefono_movil} 
            onChange={(e) => handleChange('telefono_movil', e.target.value)}
          />
        </div>
        <div>
          <Label>Fax</Label>
          <Input 
            value={formData.fax} 
            onChange={(e) => handleChange('fax', e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Email principal</Label>
          <Input 
            type="email"
            value={formData.email} 
            onChange={(e) => handleChange('email', e.target.value)}
          />
        </div>
        <div>
          <Label>Email secundario</Label>
          <Input 
            type="email"
            value={formData.email_secundario} 
            onChange={(e) => handleChange('email_secundario', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <div>
        <Label>Número de colegiado</Label>
        <Input 
          value={formData.numero_colegiado} 
          onChange={(e) => handleChange('numero_colegiado', e.target.value)}
        />
      </div>
      <div>
        <Label>Colegio profesional</Label>
        <Input 
          value={formData.colegio_profesional} 
          onChange={(e) => handleChange('colegio_profesional', e.target.value)}
          placeholder="Ej: ICAB, ICAIB..."
        />
      </div>
      <div>
        <Label>Fecha de colegiación</Label>
        <Input 
          type="date"
          value={formData.fecha_colegiacion} 
          onChange={(e) => handleChange('fecha_colegiacion', e.target.value)}
        />
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>IBAN</Label>
          <Input 
            value={formData.iban} 
            onChange={(e) => handleChange('iban', e.target.value)}
            placeholder="ES00 0000 0000 0000 0000 0000"
          />
        </div>
        <div>
          <Label>SWIFT/BIC</Label>
          <Input 
            value={formData.swift_bic} 
            onChange={(e) => handleChange('swift_bic', e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Banco</Label>
          <Input 
            value={formData.banco} 
            onChange={(e) => handleChange('banco', e.target.value)}
          />
        </div>
        <div>
          <Label>Formas de pago</Label>
          <Input 
            value={formData.formas_pago} 
            onChange={(e) => handleChange('formas_pago', e.target.value)}
            placeholder="Transferencia, efectivo..."
          />
        </div>
      </div>
      
      <div className="border-t pt-4 mt-4">
        <h4 className="font-medium mb-3">Precio / Hora</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Precio (€/hora)</Label>
            <Input 
              type="number"
              step="0.01"
              value={formData.precio_hora} 
              onChange={(e) => handleChange('precio_hora', e.target.value)}
            />
          </div>
          <div>
            <Label>Responsable</Label>
            <Input 
              value={formData.precio_hora_responsable} 
              onChange={(e) => handleChange('precio_hora_responsable', e.target.value)}
            />
          </div>
          <div>
            <Label>Tipo</Label>
            <Input 
              value={formData.precio_hora_tipo} 
              onChange={(e) => handleChange('precio_hora_tipo', e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Checkbox 
            id="facturable"
            checked={formData.facturable}
            onCheckedChange={(checked) => handleChange('facturable', checked)}
          />
          <Label htmlFor="facturable" className="cursor-pointer">Facturable</Label>
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-4">
      <div>
        <Label>Notas</Label>
        <Textarea 
          value={formData.notas} 
          onChange={(e) => handleChange('notas', e.target.value)}
          rows={3}
          placeholder="Notas internas..."
        />
      </div>
      <div>
        <Label>Observaciones</Label>
        <Textarea 
          value={formData.observaciones} 
          onChange={(e) => handleChange('observaciones', e.target.value)}
          rows={3}
        />
      </div>
      <div>
        <Label>Etiquetas</Label>
        <Input 
          value={formData.etiquetas} 
          onChange={(e) => handleChange('etiquetas', e.target.value)}
          placeholder="Separadas por comas: VIP, Urgente, Barcelona..."
        />
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>{contacto ? 'Editar Contacto' : 'Nuevo Contacto'}</DialogTitle>
        </DialogHeader>

        {/* Progress Steps */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg mb-4">
          {steps.map((s, idx) => (
            <React.Fragment key={s.num}>
              <button
                onClick={() => setStep(s.num)}
                className={`flex flex-col items-center gap-1 ${
                  step === s.num ? 'text-blue-600' : step > s.num ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s.num ? 'bg-blue-600 text-white' : 
                  step > s.num ? 'bg-green-100 text-green-600' : 'bg-gray-200'
                }`}>
                  {step > s.num ? <Check className="w-4 h-4" /> : s.num}
                </div>
                <span className="text-xs hidden sm:block">{s.title}</span>
              </button>
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-2 ${step > s.num ? 'bg-green-400' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto px-1 min-h-[300px]">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}
          {step === 6 && renderStep6()}
        </div>

        {/* Footer Navigation */}
        <DialogFooter className="border-t pt-4 mt-4">
          <div className="flex justify-between w-full">
            <Button 
              variant="outline" 
              onClick={() => step > 1 ? setStep(step - 1) : onClose()}
            >
              {step > 1 ? 'Anterior' : 'Cancelar'}
            </Button>
            <div className="flex gap-2">
              {step < 6 && (
                <Button onClick={() => setStep(step + 1)}>
                  Siguiente
                </Button>
              )}
              {step === 6 && (
                <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
                  {saving ? 'Guardando...' : (contacto ? 'Guardar cambios' : 'Crear Contacto')}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Página principal de contactos
const ContactosNew = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.rol === 'admin' || user?.rol === 'superadmin';
  const [showDuplicados, setShowDuplicados] = useState(false);
  const [showDuplicadosJuzgados, setShowDuplicadosJuzgados] = useState(false);
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTipo, setFilterTipo] = useState('all');
  const [filterEntidad, setFilterEntidad] = useState('all');
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedContacto, setSelectedContacto] = useState(null);
  
  // Paginación
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });

  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cargar datos
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      if (debouncedSearch && debouncedSearch.length >= 2) {
        params.search = debouncedSearch;
      }
      if (filterTipo && filterTipo !== 'all') {
        params.tipo = filterTipo;
      }
      if (filterEntidad && filterEntidad !== 'all') {
        params.tipo_entidad = filterEntidad;
      }
      
      const res = await contactosAPI.getAll(params);
      
      if (res.data && res.data.data) {
        setContactos(res.data.data);
        setPagination(prev => ({ ...prev, ...res.data.pagination }));
      } else {
        setContactos(Array.isArray(res.data) ? res.data : []);
      }
    } catch (error) {
      console.error('Error cargando contactos:', error);
      toast({ title: 'Error', description: 'No se pudieron cargar los contactos', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, debouncedSearch, filterTipo, filterEntidad]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filterTipo, filterEntidad]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.total_pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este contacto?')) {
      try {
        await contactosAPI.delete(id);
        toast({ title: 'Contacto eliminado' });
        loadData();
      } catch (error) {
        toast({ title: 'Error', description: 'No se pudo eliminar el contacto', variant: 'destructive' });
      }
    }
  };

  const openEditWizard = (contacto) => {
    setSelectedContacto(contacto);
    setIsWizardOpen(true);
  };

  const getTipoEntidadIcon = (tipo) => {
    switch(tipo) {
      case 'empresa': return <Building2 className="w-4 h-4 text-purple-600" />;
      case 'organismo': return <Landmark className="w-4 h-4 text-amber-600" />;
      default: return <User className="w-4 h-4 text-blue-600" />;
    }
  };

  const getTipoBadge = (tipo) => {
    const colors = {
      cliente: 'bg-blue-100 text-blue-700',
      abogado_propio: 'bg-green-100 text-green-700',
      abogado_contrario: 'bg-red-100 text-red-700',
      procurador: 'bg-purple-100 text-purple-700',
      parte_contraria: 'bg-orange-100 text-orange-700',
      perito: 'bg-teal-100 text-teal-700',
      banco: 'bg-indigo-100 text-indigo-700',
      otro: 'bg-gray-100 text-gray-700'
    };
    const label = SUBTIPOS.find(s => s.codigo === tipo)?.nombre || tipo;
    return <Badge className={`${colors[tipo] || colors.otro} hover:${colors[tipo] || colors.otro}`}>{label}</Badge>;
  };

  // Paginación UI
  const PaginationControls = () => {
    const { page, total_pages, total, has_next, has_prev, limit } = pagination;
    const startRecord = total > 0 ? (page - 1) * limit + 1 : 0;
    const endRecord = Math.min(page * limit, total);

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 bg-gray-50 border-t">
        <div className="text-sm text-gray-600">
          Mostrando <span className="font-medium">{startRecord}</span> a <span className="font-medium">{endRecord}</span> de <span className="font-medium">{total}</span> contactos
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => handlePageChange(1)} disabled={!has_prev}>
            <ChevronsLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => handlePageChange(page - 1)} disabled={!has_prev}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm px-2">Página {page} de {total_pages}</span>
          <Button variant="outline" size="sm" onClick={() => handlePageChange(page + 1)} disabled={!has_next}>
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => handlePageChange(total_pages)} disabled={!has_next}>
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contactos</h1>
          <p className="text-gray-600 mt-1">Gestión de personas, organismos y empresas</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowDuplicados(true)} data-testid="detectar-duplicados-btn" className="border-amber-300 text-amber-700 hover:bg-amber-50">
              <Merge className="w-4 h-4 mr-2" />
              Detectar duplicados
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={() => setShowDuplicadosJuzgados(true)} data-testid="detectar-duplicados-juzgados-btn" className="border-blue-300 text-blue-700 hover:bg-blue-50">
              <Landmark className="w-4 h-4 mr-2" />
              Duplicados de juzgados
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/contactos/estadisticas')}>
            <BarChart3 className="w-4 h-4 mr-2" />
            Estadísticas
          </Button>
          <Button onClick={() => { setSelectedContacto(null); setIsWizardOpen(true); }} data-testid="new-contact-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Contacto
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1 flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar en nombre, email, NIF, teléfono, dirección..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-contacts-input"
                />
              </div>
              <VoiceSearchButton
                onResult={(text) => setSearchTerm(text)}
                data-testid="voice-search-contactos"
              />
            </div>
            <Select value={filterEntidad} onValueChange={setFilterEntidad}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Tipo entidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las entidades</SelectItem>
                <SelectItem value="persona">Persona Física</SelectItem>
                <SelectItem value="organismo">Organismo</SelectItem>
                <SelectItem value="empresa">Empresa</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterTipo} onValueChange={setFilterTipo}>
              <SelectTrigger className="w-full md:w-48">
                <SelectValue placeholder="Rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los roles</SelectItem>
                {SUBTIPOS.map(s => (
                  <SelectItem key={s.codigo} value={s.codigo}>{s.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {debouncedSearch && (
            <div className="mt-2 text-sm text-gray-500">
              Buscando: "<span className="font-medium">{debouncedSearch}</span>" - {pagination.total} resultados
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rol</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">NIF/CIF</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {contactos.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                          {debouncedSearch ? 'No se encontraron contactos' : 'No hay contactos'}
                        </td>
                      </tr>
                    ) : (
                      contactos.map((c) => (
                        <tr key={c.id} className="hover:bg-gray-50" data-testid={`contact-row-${c.id}`}>
                          <td className="px-4 py-3 font-medium text-blue-600">{c.id_interno}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1" title={c.tipo_entidad}>
                              {getTipoEntidadIcon(c.tipo_entidad)}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Link to={`/contactos/${c.id}`} className="font-medium text-blue-600 hover:underline">
                              {c.nombre_completo || c.nombre}
                            </Link>
                          </td>
                          <td className="px-4 py-3">{getTipoBadge(c.tipo)}</td>
                          <td className="px-4 py-3 text-gray-600">{c.nif_cif || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">
                            {(() => {
                              const emailsList = [
                                { tipo: 'Principal', valor: c.email },
                                { tipo: 'Secundario', valor: c.email_secundario }
                              ].filter(x => x.valor);
                              if (emailsList.length === 0) return '-';
                              const [main, ...extra] = emailsList;
                              return (
                                <div className="flex items-center gap-1">
                                  <a href={`mailto:${main.valor}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-blue-600 hover:underline" title={`${main.tipo}: ${main.valor}`}>
                                    <Mail className="w-3 h-3" />
                                    <span className="truncate max-w-[200px]">{main.valor}</span>
                                  </a>
                                  {extra.length > 0 && (
                                    <Popover>
                                      <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                                        <button type="button" className="inline-flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-xs font-semibold px-1.5 h-5 min-w-[20px] hover:bg-blue-200" title="Ver todos los emails">+{extra.length}</button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-64 p-3" onClick={(e) => e.stopPropagation()}>
                                        <p className="text-xs font-semibold text-gray-500 mb-2">Todos los emails</p>
                                        <div className="space-y-2">
                                          {emailsList.map((it, i) => (
                                            <div key={i} className="flex items-start gap-2">
                                              <Mail className="w-3 h-3 mt-1 text-gray-400" />
                                              <div className="flex-1 min-w-0">
                                                <p className="text-[10px] uppercase tracking-wide text-gray-500">{it.tipo}</p>
                                                <a href={`mailto:${it.valor}`} className="text-sm text-blue-600 hover:underline break-all">{it.valor}</a>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-gray-600">
                            {(() => {
                              const telefonosList = [
                                { tipo: 'Fijo', valor: c.telefono },
                                { tipo: 'Secundario', valor: c.telefono_secundario },
                                { tipo: 'Móvil', valor: c.telefono_movil }
                              ].filter(x => x.valor);
                              if (telefonosList.length === 0) return '-';
                              const [main, ...extra] = telefonosList;
                              return (
                                <div className="flex items-center gap-1">
                                  <a href={`tel:${main.valor}`} onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 text-blue-600 hover:underline" title={`${main.tipo}: ${main.valor}`}>
                                    <Phone className="w-3 h-3" />
                                    <span>{main.valor}</span>
                                  </a>
                                  {extra.length > 0 && (
                                    <Popover>
                                      <PopoverTrigger asChild onClick={(e) => e.stopPropagation()}>
                                        <button type="button" className="inline-flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-xs font-semibold px-1.5 h-5 min-w-[20px] hover:bg-blue-200" title="Ver todos los teléfonos">+{extra.length}</button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-56 p-3" onClick={(e) => e.stopPropagation()}>
                                        <p className="text-xs font-semibold text-gray-500 mb-2">Todos los teléfonos</p>
                                        <div className="space-y-2">
                                          {telefonosList.map((it, i) => (
                                            <div key={i} className="flex items-start gap-2">
                                              <Phone className="w-3 h-3 mt-1 text-gray-400" />
                                              <div className="flex-1 min-w-0">
                                                <p className="text-[10px] uppercase tracking-wide text-gray-500">{it.tipo}</p>
                                                <a href={`tel:${it.valor}`} className="text-sm text-blue-600 hover:underline">{it.valor}</a>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button variant="ghost" size="sm" asChild>
                                <Link to={`/contactos/${c.id}`}><Eye className="w-4 h-4" /></Link>
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openEditWizard(c)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="text-red-600 hover:text-red-700">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {pagination.total > 0 && <PaginationControls />}
            </>
          )}
        </CardContent>
      </Card>

      {/* Wizard Dialog */}
      <ContactoWizard 
        isOpen={isWizardOpen} 
        onClose={() => { setIsWizardOpen(false); setSelectedContacto(null); }}
        contacto={selectedContacto}
        onSaved={loadData}
      />

      {/* Detección y fusión de duplicados (solo admin/superadmin) */}
      {isAdmin && (
        <ContactoDuplicadosDialog
          open={showDuplicados}
          onOpenChange={setShowDuplicados}
          onMerged={loadData}
        />
      )}
      {isAdmin && (
        <ContactoDuplicadosDialog
          open={showDuplicadosJuzgados}
          onOpenChange={setShowDuplicadosJuzgados}
          onMerged={loadData}
          modo="juzgados"
        />
      )}
    </div>
  );
};

export default ContactosNew;
